/**
 * The shared clock math AC-OPS-2 depends on: both the operator cockpit and the Stage window
 * receive the same `{ positionMs, positionAtServerMs }` pair (over BroadcastChannel and/or SSE)
 * and must independently extrapolate the *current* position without polling — "the client
 * extrapolates forward using the server-time offset" (SPEC playback-state schema note, and
 * AC-WAN-16). Pure and deterministic so both windows compute byte-identical numbers from
 * byte-identical inputs, which is what makes the ≤20ms sync budget achievable at all.
 */

/** How far the local clock is ahead of the server's, in ms. `GET /api/health`'s `serverTimeMs`,
 * captured around a request, gives us this once; `Date.now() + clockOffsetMs` reads server time. */
export function computeClockOffsetMs(serverTimeMs: number, requestStartMs: number, requestEndMs: number): number {
  const roundTrip = Math.max(0, requestEndMs - requestStartMs);
  const serverTimeAtRequestMidpoint = serverTimeMs;
  const localTimeAtRequestMidpoint = requestStartMs + roundTrip / 2;
  return serverTimeAtRequestMidpoint - localTimeAtRequestMidpoint;
}

export interface ExtrapolationInput {
  positionMs: number;
  positionAtServerMs: number | null;
  isPlaying: boolean;
  durationMs?: number | null;
}

/**
 * The position "right now", given a state snapshot that was true at `positionAtServerMs` (server
 * time). Paused/idle/no-timestamp states are not extrapolated (nothing to extrapolate FROM
 * outside a play run). Clamped to `[0, durationMs]` when a duration is known so a stale frame
 * arriving after a song ends can't show a runaway or negative clock.
 */
export function extrapolatePositionMs(state: ExtrapolationInput, nowMs: number, clockOffsetMs = 0): number {
  if (!state.isPlaying || state.positionAtServerMs == null) return state.positionMs;
  const serverNowMs = nowMs + clockOffsetMs;
  const elapsed = serverNowMs - state.positionAtServerMs;
  const raw = state.positionMs + Math.max(0, elapsed);
  if (state.durationMs != null && state.durationMs > 0) return Math.min(raw, state.durationMs);
  return raw;
}
